import { useQuery } from "@apollo/client";
import { useState } from "react";
import Breadcrumb from "@/components/Breadcrumb";
import EmptyState from "@/components/EmptyState";
import PageHeading from "@/components/PageHeading/PageHeading";
import { TableHeading } from "@/components/Table";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import { useMembershipServicesNavLinks } from "../../../_membershipServicesNavLinks";
import DeleteElectionDateDialog from "./_components/DeleteElectionDateDialog";
import ElectionDateDialog from "./_components/ElectionDateDialog";
import UpcomingDatesTable from "./_components/UpcomingDatesTable";
import { UPCOMING_MEMBER_ELECTION_DATES_QUERY } from "./queries";
import type { UpcomingMemberElectionDateNode, UpcomingMemberElectionDatesQueryResponse } from "./types";

type DialogState =
  | { mode: "closed" }
  | { mode: "add" }
  | { mode: "edit"; electionDate: UpcomingMemberElectionDateNode }
  | { mode: "delete"; electionDate: UpcomingMemberElectionDateNode };

export default function UpcomingMemberElectionDatesPage() {
  usePageTitle(
    "/people/membership-services/member-elections/upcoming-dates",
    "People | Upcoming Member Election Dates"
  );
  const navLinks = useMembershipServicesNavLinks();
  const { Menu, setMenu } = useTableRowMenu();

  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });

  const { data, loading, error } = useQuery<UpcomingMemberElectionDatesQueryResponse>(
    UPCOMING_MEMBER_ELECTION_DATES_QUERY,
    { skip: !canManageApplications }
  );

  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });

  const dates = data?.upcomingMemberElectionDates ?? [];

  const openAdd = () => setDialog({ mode: "add" });
  const openEdit = (electionDate: UpcomingMemberElectionDateNode) => setDialog({ mode: "edit", electionDate });
  const openDelete = (electionDate: UpcomingMemberElectionDateNode) => setDialog({ mode: "delete", electionDate });
  const closeDialog = () => setDialog({ mode: "closed" });

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Member Elections"
        compactTitle="Upcoming Member Election Dates"
        backLink={{ to: "/people/membership-services/member-elections" }}
        compact
      />
      <PageHeading title="Upcoming Member Election Dates" compact />
      <Menu />
      <div className="w-full">
        <TableHeading
          heading="Upcoming Member Election Dates"
          body={
            "Add and edit all upcoming member election dates. Eligible people will automatically be elected as members on the next upcoming date unless manually overridden."
          }
          actions={
            canManageApplications
              ? [
                  {
                    label: "Add Election Date",
                    icon: "add",
                    variant: "filled",
                    onClick: openAdd,
                    testId: "add-election-date"
                  }
                ]
              : []
          }
        />

        {!canManageApplications ? (
          <EmptyState
            iconName="lock"
            title="You Don't Have Access to This Page"
            caption="Managing member election dates requires the manage applications permission. Contact your administrator if you need access."
          />
        ) : error ? (
          <EmptyState
            iconName="error"
            title="Error"
            caption="An error occurred while loading upcoming member election dates."
          />
        ) : (
          <UpcomingDatesTable
            loading={loading}
            dates={dates}
            setMenu={setMenu}
            onAdd={openAdd}
            onEdit={openEdit}
            onDelete={openDelete}
          />
        )}
      </div>

      {canManageApplications && (
        <>
          <ElectionDateDialog
            open={dialog.mode === "add" || dialog.mode === "edit"}
            mode={dialog.mode === "edit" ? "edit" : "add"}
            initialDate={dialog.mode === "edit" ? dialog.electionDate : undefined}
            onClose={closeDialog}
          />

          <DeleteElectionDateDialog
            open={dialog.mode === "delete"}
            electionDate={dialog.mode === "delete" ? dialog.electionDate : null}
            onClose={closeDialog}
          />
        </>
      )}
    </AppLayout>
  );
}
